import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Icon } from '@/components/ui';
import { ModeSwitch } from './ModeSwitch';
import { CommandPalette } from './CommandPalette';
import { PreferencesModal } from './PreferencesModal';
import { useModeStore } from '@/stores/modeStore';
import { api } from '@/lib/api';
import { Lab } from '@/lib/toast';

export interface TopBarProps {
  title?: string;
  subtitle?: string;
  onLaunch?: () => void;
}

/**
 * Top bar. Left = page title + short description. Right = four
 * visually-harmonized controls (all 32 px tall) :
 *   1. Search stub (opens CommandPalette on click or ⌘K)
 *   2. Sim/Réel switch
 *   3. Stop split-button (only while any run is active)
 *   4. Launch button (accent)
 *
 * The global stop control is ALWAYS surfaced when at least one campagne is
 * running, regardless of the current route — operators can abort from any
 * page without having to navigate to Live first. Clicking the primary action
 * stops the live currently targeted by the URL if we are on /live, otherwise
 * jumps to Live first ; the chevron exposes the platform-wide kill switch.
 */
export function TopBar({ title, subtitle, onLaunch }: TopBarProps) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  const qc = useQueryClient();
  const { mode, setMode } = useModeStore();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const { data: runs = [] } = useQuery({
    queryKey: ['runs', 'topbar-stop'],
    queryFn: () => api.listRuns({ limit: 30 }),
    refetchInterval: 2_000,  // faster detection for the emergency STOP button
  });
  const activeRuns = runs.filter((r) => r.status === 'running' || r.status === 'stopping');
  const activeCount = activeRuns.length;
  const currentLiveRun = location.pathname.startsWith('/live')
    ? activeRuns[0]?.run_id
    : undefined;

  const emergencyStopAll = async () => {
    const ok = await Lab.confirm(
      `Arrêt d'urgence : ${activeCount} campagne${activeCount > 1 ? 's ser' : ' ser'}${activeCount > 1 ? 'ont' : 'a'} tuée${activeCount > 1 ? 's' : ''} immédiatement. Confirmer ?`,
      "STOP D'URGENCE",
      { danger: true },
    );
    if (!ok) return;
    try {
      const res = await api.stopAllRuns('hard');
      Lab.toast(
        `${res.count} campagne${res.count > 1 ? 's' : ''} arrêtée${res.count > 1 ? 's' : ''} (kill immédiat).`,
        'pass',
      );
      invalidate();
    } catch (e) {
      Lab.toast(`Échec du stop d'urgence : ${(e as Error).message}`, 'error');
    }
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['runs'] });
  };
  // Note : les actions stopCurrent / stopAll par mode (safe/hard) ne sont plus
  // exposées dans le topbar simplifié — le bouton "Stop d'urgence" ci-dessous
  // déclenche un kill immédiat (hard), suffisant pour le cas d'usage. Le
  // contrôle granulaire reste accessible depuis la page Live.

  // Layout unifié desktop + mobile :
  //   [titre+desc compact 2 lignes]  [recherche icône]  [dropdown mode+lancer]
  // Tous les contrôles à droite font 36×36px (= hauteur du burger sidebar).
  // Le bandeau global lui-même fait 36px de haut max — règle dure du UX.

  const CTRL_SIZE = 36; // hauteur cible de tous les contrôles

  const TitleBlock = (
    <div style={{
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      minWidth: 0, flex: '1 1 auto',
      gap: 1,
      lineHeight: 1.1,
    }}>
      {title && (
        <span
          title={title}
          style={{
            fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            lineHeight: 1.15,
          }}
        >
          {title}
        </span>
      )}
      {subtitle && (
        <span
          title={subtitle}
          style={{
            fontSize: 11, color: 'var(--text-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            lineHeight: 1.15,
          }}
        >
          {subtitle}
        </span>
      )}
    </div>
  );

  // Bouton recherche : carré, icône loupe seule (gain de place).
  // CommandPalette s'ouvre au clic ou avec ⌘K, plus besoin du raccourci visible.
  const SearchButton = (
    <button
      type="button"
      onClick={() => setPaletteOpen(true)}
      title="Rechercher (⌘ K)"
      aria-label="Rechercher dans la plateforme"
      className="topbar-icon-btn"
      style={{
        width: CTRL_SIZE, height: CTRL_SIZE,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--surface-0)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        color: 'var(--text-secondary)',
        cursor: 'pointer', flexShrink: 0,
        padding: 0,
      }}
    >
      <Icon name="search" size={16} />
    </button>
  );

  // Badge live (compact, intégré dans la barre quand un run tourne).
  const LiveBadge = activeCount > 0 && (
    <span
      className="badge run mono"
      style={{ padding: '2px 6px', fontSize: 10, flexShrink: 0, alignSelf: 'center' }}
      title={currentLiveRun ?? `${activeCount} campagne${activeCount > 1 ? 's' : ''} en cours`}
    >
      {activeCount} live
    </span>
  );

  // Bouton Stop discret (rouge, petit) quand un run tourne. Pas dans le dropdown
  // car c'est une action critique qui doit rester accessible en 1 clic.
  const StopButton = activeCount > 0 && (
    <button
      type="button"
      onClick={emergencyStopAll}
      title={`Stop d'urgence — tuer les ${activeCount} campagne${activeCount > 1 ? 's' : ''}`}
      aria-label="Stop d'urgence"
      className="lab-emergency-stop topbar-icon-btn"
      style={{
        width: CTRL_SIZE, height: CTRL_SIZE,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--danger)',
        background: 'var(--surface-0)',
        color: 'var(--danger)',
        cursor: 'pointer', flexShrink: 0,
        padding: 0,
        animation: 'lab-emergency-pulse 1.6s ease-in-out infinite',
      }}
    >
      <Icon name="stop" size={16} />
    </button>
  );

  return (
    <>
      <div className="topbar topbar-compact">
        {/* Spacer pour le burger : 36px (bouton) + 4 (left) + 6 (gap) = 46px.
         * Le titre commence ainsi juste à droite du burger, sans trou visuel. */}
        <div style={{ flex: '0 0 46px', minWidth: 0 }} aria-hidden />

        {TitleBlock}

        {LiveBadge}

        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {SearchButton}
          {StopButton}
          <ActionsDropdown
            mode={mode}
            setMode={setMode}
            onLaunch={onLaunch}
            ctrlSize={CTRL_SIZE}
          />
        </div>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
}

// ─── ActionsDropdown ───────────────────────────────────────────────────
// Bouton ⋯ qui ouvre un panneau ancré contenant le switch sim/réel et le
// bouton Lancer. Permet de garder une topbar de 36px tout en exposant ces
// 2 actions importantes. Cliquer en dehors ferme le panneau.

interface ActionsDropdownProps {
  mode: 'simulation' | 'real';
  setMode: (m: 'simulation' | 'real') => void;
  onLaunch?: () => void;
  ctrlSize: number;
}

// `setMode` est passé pour cohérence d'API (réserve future) mais le ModeSwitch
// utilise directement le store. ESLint ignoré via void usage ci-dessous si besoin.
function ActionsDropdown({ mode, onLaunch, ctrlSize }: ActionsDropdownProps) {
  const [open, setOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const dotColor = mode === 'real' ? 'var(--success)' : 'var(--info)';

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Mode d'exécution & lancement"
        aria-label="Ouvrir les actions"
        aria-expanded={open}
        className="topbar-icon-btn"
        style={{
          width: 'auto', height: ctrlSize,
          minWidth: ctrlSize,
          padding: '0 10px',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          background: 'var(--surface-0)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
        }}
      >
        {/* Pastille de couleur reflétant le mode courant pour info instantanée */}
        <span aria-hidden style={{
          width: 8, height: 8, borderRadius: '50%', background: dotColor,
        }} />
        <Icon name="play" size={12} />
        <Icon name="chev" size={10} style={{ transform: open ? 'rotate(180deg)' : undefined }} />
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
            background: 'var(--surface-1)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--elev-3)',
            padding: 10,
            display: 'flex', flexDirection: 'column', gap: 8,
            minWidth: 200,
            zIndex: 1500,
          }}
        >
          <div className="label" style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>
            Mode d'exécution
          </div>
          <ModeSwitch />
          <div style={{ height: 1, background: 'var(--border-subtle)', margin: '4px 0' }} />
          {onLaunch ? (
            <button
              type="button"
              onClick={() => { setOpen(false); onLaunch(); }}
              className="ctrl primary"
              style={{
                height: 36, padding: '0 14px',
                whiteSpace: 'nowrap', gap: 6,
                justifyContent: 'center',
              }}
            >
              <Icon name="play" size={12} />
              <span>Lancer une campagne</span>
            </button>
          ) : (
            <span style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', padding: '8px 0' }}>
              Lancement non disponible sur cette page
            </span>
          )}
          <div style={{ height: 1, background: 'var(--border-subtle)', margin: '4px 0' }} />
          <button
            type="button"
            onClick={() => { setOpen(false); setPrefsOpen(true); }}
            className="ctrl"
            style={{
              height: 36, padding: '0 14px',
              whiteSpace: 'nowrap', gap: 6,
              justifyContent: 'flex-start',
              background: 'transparent',
              border: '1px solid transparent',
            }}
          >
            <Icon name="settings" size={14} />
            <span>Préférences</span>
          </button>
        </div>
      )}
      <PreferencesModal open={prefsOpen} onClose={() => setPrefsOpen(false)} />
    </div>
  );
}
